import AcUnitIcon from "@mui/icons-material/AcUnit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import SnoozeIcon from "@mui/icons-material/Snooze";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
	Button,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	Cooldown as CooldownType,
	deleteCooldown,
	getCooldowns,
	patchCooldown,
	setCooldown,
} from "../../api/cooldown";
import {
	formatDate,
	formatDateTime,
	formatRelativeTime,
} from "../../utils/date-formatting";
import {
	COOLDOWN_SHORTCUTS,
	DatePickerDialog,
	getDefaultFutureDate,
} from "../common/DatePickerDialog";
import { useGlobalState } from "../globalState";

export const Cooldown = ({ personId }: { personId: number }) => {
	const { currentUser } = useGlobalState();
	const [cooldowns, setCooldowns] = useState<CooldownType[]>([]);
	const [showExpired, setShowExpired] = useState<boolean>(false);

	// Add dialog state
	const [addDialogOpen, setAddDialogOpen] = useState(false);

	// Snooze dialog state
	const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
	const [snoozingCooldown, setSnoozingCooldown] = useState<CooldownType | null>(null);

	useEffect(() => {
		getCooldowns({ person: personId, hideHistory: false }).then((res) => {
			setCooldowns(res.results);
		});
	}, [personId]);

	const handleOpenAddDialog = () => {
		setAddDialogOpen(true);
	};

	const handleCloseAddDialog = () => {
		setAddDialogOpen(false);
	};

	const handleSaveCooldown = (selectedDate: Date) => {
		const data: CooldownType = {
			person: personId,
			until: selectedDate.toISOString(),
			user: currentUser.id,
		};

		setCooldown(data).then((res) => {
			setCooldowns([...cooldowns, res]);
			setAddDialogOpen(false);
		});
	};

	const handleDeleteCooldown = (cooldown: CooldownType) => {
		deleteCooldown({ cooldownId: cooldown.id }).then(() => {
			setCooldowns(cooldowns.filter((c) => c.id !== cooldown.id));
		});
	};

	const handleOpenSnoozeDialog = (cooldown: CooldownType) => {
		setSnoozingCooldown(cooldown);
		setSnoozeDialogOpen(true);
	};

	const handleCloseSnoozeDialog = () => {
		setSnoozeDialogOpen(false);
		setSnoozingCooldown(null);
	};

	const handleSaveSnooze = (snoozeDate: Date) => {
		if (!snoozingCooldown) return;
		patchCooldown({
			cooldownId: snoozingCooldown.id,
			person: snoozingCooldown.person,
			user: snoozingCooldown.user,
			until: snoozeDate.toISOString(),
		}).then((res) => {
			setCooldowns(
				cooldowns.map((c) => (c.id === snoozingCooldown.id ? res : c))
			);
			setSnoozeDialogOpen(false);
			setSnoozingCooldown(null);
		});
	};

	const activeCooldowns = cooldowns
		.filter((c) => new Date(c.until) >= new Date())
		.sort((a, b) => new Date(a.until).getTime() - new Date(b.until).getTime());

	const expiredCooldowns = cooldowns
		.filter((c) => new Date(c.until) < new Date())
		.sort((a, b) => new Date(b.until).getTime() - new Date(a.until).getTime());

	const displayCooldowns = showExpired
		? [...activeCooldowns, ...expiredCooldowns]
		: activeCooldowns;

	const getUntilDisplay = (cooldown: CooldownType) => {
		const isExpired = new Date(cooldown.until) < new Date();
		const relTime = formatRelativeTime(cooldown.until);
		const dateStr = formatDate(cooldown.until);
		const timeStr = isExpired ? `${relTime} ago` : `in ${relTime}`;
		return `${dateStr} (${timeStr})`;
	};

	return (
		<>
			<TableContainer data-component="cooldowns-table">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Status</TableCell>
							<TableCell>User</TableCell>
							<TableCell>Set</TableCell>
							<TableCell>Until</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{displayCooldowns.length === 0 ? (
							<TableRow data-component="no-cooldowns-row">
								<TableCell colSpan={5} align="center">
									No cooldowns
								</TableCell>
							</TableRow>
						) : (
							displayCooldowns.map((cooldown) => {
								const isExpired = new Date(cooldown.until) < new Date();
								return (
									<TableRow
										key={cooldown.id}
										data-component="cooldown-row"
										sx={{ opacity: isExpired ? 0.6 : 1 }}
									>
										<TableCell>
											{isExpired ? (
												<Tooltip title="Expired">
													<LocalFireDepartmentIcon
														sx={{ color: "warning.light" }}
														fontSize="small"
													/>
												</Tooltip>
											) : (
												<Tooltip title="Active">
													<AcUnitIcon
														sx={{ color: "info.main" }}
														fontSize="small"
													/>
												</Tooltip>
											)}
										</TableCell>
										<TableCell>
											{cooldown.user_first_name}
										</TableCell>
										<TableCell>
											{cooldown.created && (
												<>
													{formatDateTime(cooldown.created)}
													{cooldown.updated &&
														formatDateTime(
															cooldown.updated
														) !==
															formatDateTime(
																cooldown.created
															) && (
															<Tooltip
																title={`Updated: ${formatDateTime(cooldown.updated)}`}
															>
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{
																		display:
																			"block",
																	}}
																>
																	Updated:{" "}
																	{formatDateTime(
																		cooldown.updated
																	)}
																</Typography>
															</Tooltip>
														)}
												</>
											)}
										</TableCell>
										<TableCell>
											{getUntilDisplay(cooldown)}
										</TableCell>
										<TableCell align="right">
											<Stack
												direction="row"
												spacing={0.5}
												justifyContent="flex-end"
											>
												<Tooltip title="Extend">
													<IconButton
														data-component="extend-cooldown-button"
														size="small"
														onClick={() =>
															handleOpenSnoozeDialog(
																cooldown
															)
														}
													>
														<SnoozeIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton
														data-component="delete-cooldown-button"
														size="small"
														onClick={() =>
															handleDeleteCooldown(
																cooldown
															)
														}
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</Stack>
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Stack
				direction="row"
				justifyContent="flex-end"
				alignItems="center"
				spacing={1}
				sx={{ mt: 2 }}
				data-component="cooldown-footer"
			>
				{expiredCooldowns.length > 0 && (
					<Button
						variant="outlined"
						size="small"
						startIcon={
							showExpired ? <VisibilityOffIcon /> : <VisibilityIcon />
						}
						onClick={() => setShowExpired(!showExpired)}
						sx={{ mr: "auto" }}
						data-component="toggle-expired-button"
					>
						{showExpired ? "Hide expired" : "Show expired"}
					</Button>
				)}
				<Button
					variant="outlined"
					size="small"
					startIcon={<AcUnitIcon />}
					onClick={handleOpenAddDialog}
					data-component="add-cooldown-button"
				>
					Add Cooldown
				</Button>
			</Stack>

			<DatePickerDialog
				open={addDialogOpen}
				onClose={handleCloseAddDialog}
				onSave={handleSaveCooldown}
				title="Add Cooldown"
				initialDate={getDefaultFutureDate(7)}
				shortcuts={COOLDOWN_SHORTCUTS}
				dataComponent="add-cooldown-dialog"
			/>

			<DatePickerDialog
				open={snoozeDialogOpen}
				onClose={handleCloseSnoozeDialog}
				onSave={handleSaveSnooze}
				title="Extend Cooldown"
				saveButtonText="Extend"
				initialDate={getDefaultFutureDate(7)}
				shortcuts={COOLDOWN_SHORTCUTS}
				dataComponent="extend-cooldown-dialog"
			/>
		</>
	);
};
